import { useMemo, useState } from 'react';
import { CheckCircle2, Eye, EyeOff, List, Lock } from 'lucide-react';
import { Field, Input } from '@/components/ui/Field';
import { cn } from '@/lib/cn';
import { PASSWORD_RULES, evaluatePassword } from './passwordRules';

/**
 * A new password, its confirmation, and the strength rules it has to meet.
 *
 * Shared by resetting a password and by accepting an invitation, so the two
 * ways of choosing a password cannot come to enforce different rules.
 */
export function NewPasswordFields({
  password,
  confirmation,
  onPassword,
  onConfirmation,
  label = 'New password',
}: {
  password: string;
  confirmation: string;
  onPassword: (value: string) => void;
  onConfirmation: (value: string) => void;
  label?: string;
}) {
  const [revealPassword, setRevealPassword] = useState(false);
  const [revealConfirmation, setRevealConfirmation] = useState(false);
  const strength = useMemo(() => evaluatePassword(password), [password]);
  const mismatch = confirmation.length > 0 && password !== confirmation;

  const reveal = (shown: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      aria-label={shown ? 'Hide password' : 'Show password'}
      className="rounded p-1.5 text-muted transition-colors hover:bg-cloud hover:text-body"
    >
      {shown ? <EyeOff aria-hidden className="h-4 w-4" /> : <Eye aria-hidden className="h-4 w-4" />}
    </button>
  );

  return (
    <>
      <Field label={label} htmlFor="password">
        <Input
          id="password"
          name="password"
          type={revealPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          placeholder="Create a strong password"
          leadingIcon={<Lock />}
          value={password}
          onChange={(event) => onPassword(event.target.value)}
          trailingSlot={reveal(revealPassword, () => setRevealPassword((value) => !value))}
        />
      </Field>

      <Field label={`Confirm ${label.toLowerCase()}`} htmlFor="password_confirmation" error={mismatch ? 'Passwords do not match.' : undefined}>
        <Input
          id="password_confirmation"
          name="password_confirmation"
          type={revealConfirmation ? 'text' : 'password'}
          autoComplete="new-password"
          required
          placeholder="Repeat the password"
          leadingIcon={<Lock />}
          state={mismatch ? 'error' : confirmation && !mismatch ? 'success' : 'default'}
          value={confirmation}
          onChange={(event) => onConfirmation(event.target.value)}
          trailingSlot={reveal(revealConfirmation, () => setRevealConfirmation((value) => !value))}
        />
      </Field>

      <div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-helper font-semibold text-body">Password Strength</span>
          <span
            className={cn(
              'text-helper font-semibold',
              strength.score >= 4 && 'text-signal-700',
              strength.score === 3 && 'text-digital-600',
              strength.score === 2 && 'text-gold-700',
              strength.score <= 1 && 'text-danger-600',
            )}
          >
            {strength.label}
          </span>
        </div>
        <div role="progressbar" aria-valuemin={0} aria-valuemax={4} aria-valuenow={strength.score} aria-label="Password strength" className="mt-2 flex gap-1.5">
          {[1, 2, 3, 4].map((segment) => (
            <span key={segment} className={cn('h-1 flex-1 rounded-full transition-colors', segment <= strength.score ? strength.colorClass : 'bg-pale-blue')} />
          ))}
        </div>

        <ul className="mt-4 space-y-2">
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(password, {});
            return (
              <li key={rule.id} className="flex items-center gap-2.5">
                {met ? <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0 text-signal" /> : <List aria-hidden className="h-4 w-4 shrink-0 text-muted/50" />}
                <span className={cn('text-helper', met ? 'text-body' : 'text-muted')}>{rule.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
